import { Alert, Badge, Group, Paper, Table, Text, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { rawApi } from '../../api/client';
import { EmptyNote } from '../../components/RecordScreen';
import { formatDateTime, formatMoney } from '../../lib/format';

export const EXCEPTIONS_PATH = '/admin/v1/reports/exceptions';

interface ExceptionRow {
  at: string;
  businessDate: string;
  discountName: string;
  kind: 'comp' | 'discount' | 'priceBreak' | 'staffMeal' | 'houseTab';
  itemName: string;
  amount: { currency: string; minor: number };
  amountBaseMinor: number;
  appliedByName: string | null;
  approvedByName: string | null;
  verification: 'ownPin' | 'externalNoPin' | null;
  clockStanding: 'clockedOut' | 'clockedIn' | 'onBreak' | null;
  reasonName: string | null;
  runningBaseMinor: number | null;
}

interface ExceptionDay {
  businessDate: string;
  rows: ExceptionRow[];
  totalBaseMinor: number;
  capBaseMinor: number | null;
}

interface ExceptionsReport {
  from: string;
  to: string;
  baseCurrency: string;
  staffMealDailyCapMinor: number;
  performerDailyCapMinor: number | null;
  beneficiaries: {
    name: string;
    kind: 'employee' | 'externalEmployee' | 'houseAccount' | 'none';
    days: ExceptionDay[];
    totalBaseMinor: number;
  }[];
  performers: {
    name: string;
    days: { businessDate: string; totalBaseMinor: number; overCeiling: boolean }[];
    totalBaseMinor: number;
  }[];
}

const KIND_WORDS: Record<ExceptionRow['kind'], string> = {
  comp: 'Comp',
  discount: 'Discount',
  priceBreak: 'Price break',
  staffMeal: 'Staff meal',
  houseTab: 'House tab',
};

const STANDING_WORDS: Record<NonNullable<ExceptionRow['clockStanding']>, string> = {
  clockedIn: 'On the clock',
  clockedOut: 'Off the clock',
  onBreak: 'On break',
};

function ConfirmedBadge({ row }: { row: ExceptionRow }) {
  if (row.verification === 'ownPin') {
    return (
      <Badge color="teal" variant="light" size="sm">
        Own PIN
      </Badge>
    );
  }
  if (row.verification === 'externalNoPin') {
    return (
      <Badge color="grape" variant="light" size="sm">
        Sea Hunt — no PIN
      </Badge>
    );
  }
  return (
    <Text size="sm" c="dimmed">
      —
    </Text>
  );
}

/**
 * Comps, price breaks and staff discounts, by beneficiary then day — the page every giveaway rule
 * in the register is aimed at. The guards make the rows true; this is where the owner reads them.
 *
 * The clock column reports rather than polices: a staff meal rung off the clock is a visible fact
 * here, not a refusal at the bar, until the owner switches the clock requirement on. The running
 * column shows each person's day marching toward the allowance the register refuses past.
 */
export function Exceptions({ from, to }: { from: string; to: string }) {
  const report = useQuery({
    queryKey: ['exceptions-report', from, to],
    queryFn: () =>
      rawApi<ExceptionsReport>(
        'GET',
        `${EXCEPTIONS_PATH}?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      ),
    enabled: Boolean(from && to),
  });

  const data = report.data;
  const base = data?.baseCurrency ?? 'USD';
  const anyRows = (data?.beneficiaries.length ?? 0) > 0;

  return (
    <Paper withBorder p="md" radius="md" mb="md">
      <Title order={5}>Comps and staff discounts</Title>
      <Text size="sm" c="dimmed" mt={2} mb="sm" maw={880}>
        Everything that left the bar for less than the menu price in these dates, grouped by the person it was
        for. Each staff meal shows how it was confirmed — that person's own PIN, or a Sea Hunt employee
        recorded without one — where their time clock stood, and their day's running total against the{' '}
        {formatMoney(data?.staffMealDailyCapMinor, base)} allowance. Giveaways with no named person group at
        the end under whoever rang them.
      </Text>

      {report.isError && <Alert color="red">That report could not be loaded.</Alert>}
      {!report.isLoading && !report.isError && !anyRows && (
        <EmptyNote>Nothing was comped or discounted in these dates.</EmptyNote>
      )}

      {data?.beneficiaries.map((person) => (
        <Paper withBorder p="sm" radius="md" mb="sm" key={`${person.kind}:${person.name}`}>
          <Group gap="xs" mb={4}>
            <Text fw={600}>{person.name}</Text>
            {person.kind === 'externalEmployee' && (
              <Badge color="grape" variant="light" size="sm">
                Sea Hunt Divers
              </Badge>
            )}
            {person.kind === 'houseAccount' && (
              <Badge color="blue" variant="light" size="sm">
                House tab
              </Badge>
            )}
            {person.kind === 'none' && (
              <Badge color="gray" variant="light" size="sm">
                No named person
              </Badge>
            )}
            <Text size="sm" c="dimmed">
              {formatMoney(person.totalBaseMinor, base)} across these dates
            </Text>
          </Group>
          {person.days.map((day) => (
            <div key={day.businessDate}>
              <Group gap="xs" mt="xs" mb={4}>
                <Text size="sm" fw={500}>
                  {day.businessDate}
                </Text>
                <Text
                  size="sm"
                  c={day.capBaseMinor !== null && day.totalBaseMinor > day.capBaseMinor ? 'red' : 'dimmed'}
                >
                  {formatMoney(day.totalBaseMinor, base)}
                  {day.capBaseMinor !== null ? ` of ${formatMoney(day.capBaseMinor, base)}` : ''}
                </Text>
              </Group>
              <Table.ScrollContainer minWidth={760}>
                <Table striped highlightOnHover withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>When</Table.Th>
                      <Table.Th>What</Table.Th>
                      <Table.Th>Given as</Table.Th>
                      <Table.Th>Amount</Table.Th>
                      <Table.Th>Rung by</Table.Th>
                      <Table.Th>Confirmed</Table.Th>
                      <Table.Th>Clock</Table.Th>
                      <Table.Th>Reason</Table.Th>
                      <Table.Th>Running</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {day.rows.map((row) => (
                      <Table.Tr key={row.at + row.itemName + String(row.amount.minor)}>
                        <Table.Td>{formatDateTime(row.at)}</Table.Td>
                        <Table.Td>{row.itemName}</Table.Td>
                        <Table.Td>
                          {KIND_WORDS[row.kind]} — {row.discountName}
                        </Table.Td>
                        <Table.Td>{formatMoney(row.amount.minor, row.amount.currency)}</Table.Td>
                        <Table.Td>{row.appliedByName ?? '—'}</Table.Td>
                        <Table.Td>
                          <ConfirmedBadge row={row} />
                        </Table.Td>
                        <Table.Td>{row.clockStanding ? STANDING_WORDS[row.clockStanding] : '—'}</Table.Td>
                        <Table.Td>{row.reasonName ?? '—'}</Table.Td>
                        <Table.Td>
                          {row.runningBaseMinor !== null ? formatMoney(row.runningBaseMinor, base) : '—'}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </div>
          ))}
        </Paper>
      ))}

      {(data?.performers.length ?? 0) > 0 && (
        <>
          <Title order={6} mt="md">
            Given away per person ringing, per day
          </Title>
          <Text size="sm" c="dimmed" mt={2} mb="xs" maw={880}>
            Ordinary comps, discounts and price breaks added up by the person who rang them — staff meals and
            the house tab answer to their own rules and are not counted here. A red figure passed the{' '}
            {data?.performerDailyCapMinor !== null
              ? formatMoney(data?.performerDailyCapMinor, base)
              : 'unset'}{' '}
            daily ceiling the register now refuses past.
          </Text>
          <Table withTableBorder striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Who</Table.Th>
                <Table.Th>Day</Table.Th>
                <Table.Th>Given away</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data?.performers.flatMap((p) =>
                p.days.map((d) => (
                  <Table.Tr key={`${p.name}:${d.businessDate}`}>
                    <Table.Td>{p.name}</Table.Td>
                    <Table.Td>{d.businessDate}</Table.Td>
                    <Table.Td>
                      <Text size="sm" c={d.overCeiling ? 'red' : undefined}>
                        {formatMoney(d.totalBaseMinor, base)}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )),
              )}
            </Table.Tbody>
          </Table>
        </>
      )}
    </Paper>
  );
}
